import { Button } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useCreateProcurementForNeedMutation,
  useDeleteSourcingEventMutation,
  useGetSourcingEventQuery,
  useListSourcingEventFieldSourcesQuery,
  useSaveInterviewDraftMutation,
} from "../../autogen/bff-api";
import { urls } from "../../urls";
import { useRunsPublicProcurements } from "../organization/useRunsPublicProcurements";
import { useSub } from "../subscription/useSub";
import { ChooseEventDepartmentModal } from "../departments/ChooseEventDepartmentModal";
import { useCreateSourcingEvent } from "../departments/useCreateSourcingEvent";
import { useApiError } from "../errors/useApiError";
import { AssistantChat, type EmptyConversationActions } from "./AssistantChat";
import {
  BASIS_CONTRACT_PARAM,
  BASIS_PROCUREMENT_PARAM,
  NEED_PARAM,
  PROCUREMENT_PARAM,
} from "./createProcurementParams";
import { CreationProgress } from "./CreationProgress";
import { useOptionalAssistantContext } from "./AssistantProvider";
import { AssistantPanelContext } from "./assistantPanelContext";
import { hasSettledRegulation, interviewPosition } from "./creationAreas";
import { panelFillsTheScreen } from "./panelPlacement";
import { useViewport } from "../hooks/useViewport";
import type { RegulationConfirmation } from "./regulationConfirmation";
import { RegulationConfirmationCard, useRegulationConfirmation } from "./RegulationConfirmationCard";
import { useUnsavedInterviewGuard } from "./useUnsavedInterviewGuard";
import { isInterviewOpenElsewhere, useInterviewPresence } from "./interviewPresence";
import { WhatDoYouHave, type Starting } from "./WhatDoYouHave";

type Opening = { search: string; text: string; label: string; send: EmptyConversationActions["send"] };

export const CreateProcurementWithAi = ({ projectId }: { projectId: string | null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { trigger } = useApiError();
  const [createProcurementForNeed, { isLoading: isCreatingFromNeed }] = useCreateProcurementForNeedMutation();
  const projects = useMemo(() => (projectId ? [projectId] : undefined), [projectId]);
  const fillInYourself = useCreateSourcingEvent(
    (eventId) => navigate(urls.app.sourcingEvents.edit.go(eventId).info),
    projects,
  );
  const [opening, setOpening] = useState<Opening | null>(null);
  const procurementId = new URLSearchParams(location.search).get(PROCUREMENT_PARAM);
  const [confirmedRegulationFor, setConfirmedRegulationFor] = useState<string[]>([]);
  const [reopenedRegulationFor, setReopenedRegulationFor] = useState<string[]>([]);
  const regulation = useRegulationConfirmation(procurementId);
  const confirmation = regulation
    ? reopenedRegulationFor.includes(regulation.event.id)
      ? regulation.reopened
      : regulation.confirmation
    : null;
  const assistant = useOptionalAssistantContext();
  const panel = useContext(AssistantPanelContext);
  const sub = useSub();
  const runsPublicProcurements = useRunsPublicProcurements();
  const eventQuery = useGetSourcingEventQuery(procurementId ? { eventId: procurementId, recordView: false } : skipToken);
  const sourcesQuery = useListSourcingEventFieldSourcesQuery(procurementId ? { eventId: procurementId } : skipToken);
  const event = eventQuery.data;
  const sources = sourcesQuery.data;
  const procurementFailedToLoad = eventQuery.isError || sourcesQuery.isError;
  const [saveInterviewDraft, { isLoading: isSavingDraft }] = useSaveInterviewDraftMutation();
  const [deleteProcurement] = useDeleteSourcingEventMutation();
  const savedByInterview = !!event?.interview?.saved && hasSettledRegulation(event);
  const regulationConfirmed = (eventId: string) =>
    confirmedRegulationFor.includes(eventId) || (savedByInterview && !reopenedRegulationFor.includes(eventId));
  const awaitsRegulation = !!regulation && !!confirmation && !regulationConfirmed(regulation.event.id);
  const setRegulationPending = assistant?.setRegulationPending;
  const position =
    event && sources
      ? interviewPosition(event, sources.fields, {
          hasPublicProcurementAccess: runsPublicProcurements,
          hasProductAccess: sub.hasProductAccess,
        })
      : null;
  const isUnsaved = procurementId ? event?.interview?.saved === false : (assistant?.turns.length ?? 0) > 0;
  const interviewIsDone = !!event && !isUnsaved && !!position?.done;
  const isTurnSettled = !assistant?.isRunning && !assistant?.pendingConfirmation;
  const reopenPanel = panel?.reopenPanel;
  const closePanel = panel?.closePanel;
  const viewport = useViewport();
  const panelWouldCoverSummary = panelFillsTheScreen(viewport.width, viewport.remPx);

  const saveAsDraft = (eventId: string) =>
    saveInterviewDraft({
      eventId,
      saveInterviewDraftRequest: { conversationId: assistant?.activeConversationId ?? undefined },
    }).unwrap();

  const continueInForm = async () => {
    if (!procurementId || (!position && !procurementFailedToLoad)) return;
    try {
      if (isUnsaved || !event) await saveAsDraft(procurementId);
      navigate(urls.app.sourcingEvents.edit.go(procurementId)[position?.step ?? "info"]);
    } catch (e) {
      trigger(e);
    }
  };

  const leaveWithoutSaving = async (keptInOtherTab: boolean) => {
    try {
      if (procurementId && isUnsaved && !keptInOtherTab) {
        await deleteProcurement({ eventId: procurementId }).unwrap();
      }
    } catch (e) {
      trigger(e);
      throw e;
    }
    navigate(urls.app.sourcingEvents.viewAll);
  };
  useInterviewPresence(isUnsaved ? procurementId : null);
  const leaveGuard = useUnsavedInterviewGuard(isUnsaved, leaveWithoutSaving, () =>
    procurementId ? isInterviewOpenElsewhere(procurementId) : false,
  );

  const summaryOfDoneInterview = interviewIsDone && isTurnSettled && event && position ? event.id : null;
  const summaryStep = position?.step;
  useEffect(() => {
    if (!summaryOfDoneInterview || !summaryStep) return;
    if (panelWouldCoverSummary) closePanel?.();
    else reopenPanel?.();
    navigate(urls.app.sourcingEvents.edit.go(summaryOfDoneInterview)[summaryStep]);
  }, [summaryOfDoneInterview, summaryStep, reopenPanel, closePanel, panelWouldCoverSummary, navigate]);

  useEffect(() => {
    if (!setRegulationPending) return;
    setRegulationPending(awaitsRegulation);
    return () => setRegulationPending(false);
  }, [setRegulationPending, awaitsRegulation]);

  useEffect(() => {
    if (!opening || location.search !== opening.search) return;
    opening.send(opening.text, opening.label);
    setOpening(null);
  }, [opening, location.search]);

  const openWith = (references: Record<string, string>, text: string, label: string, send: Opening["send"]) => {
    const query = new URLSearchParams(location.search);
    Object.entries(references).forEach(([param, value]) => query.set(param, value));
    const search = `?${query.toString()}`;
    navigate({ pathname: location.pathname, search }, { replace: true });
    setOpening({ search, text, label, send });
  };

  const confirmedRegulationMessage = (confirmed: RegulationConfirmation, procedure: string | null): string => {
    if (confirmed.kind === "private") {
      return t("I have confirmed that this is a private competition, without a public procedure. Continue with the next area.");
    }
    if (confirmed.kind === "privatePartI") {
      return t("I have confirmed that this is a private competition in Part I, without a fixed procedure. Continue with the next area.");
    }
    return confirmed.classification
      ? t("I have confirmed {{part}} and the procedure «{{procedure}}». Continue with the next area.", {
          part: confirmed.classification,
          procedure,
        })
      : t("I have confirmed the procedure «{{procedure}}». Continue with the next area.", { procedure });
  };

  const start = async (starting: Starting, actions: EmptyConversationActions) => {
    switch (starting.kind) {
      case "need": {
        try {
          const need = await createProcurementForNeed({
            needId: starting.id,
            createProcurementForNeedRequest: { attachmentIds: [], inInterview: true },
          }).unwrap();
          const procurementId = need.procurement?.id;
          if (!procurementId) return;
          openWith(
            { [NEED_PARAM]: starting.id, [PROCUREMENT_PARAM]: procurementId },
            t("I have created a procurement from the need «{{need}}». Help me fill it in, one step at a time.", {
              need: starting.title,
            }),
            t("A need"),
            actions.send,
          );
        } catch (e) {
          trigger(e);
        }
        return;
      }
      case "procurement":
        openWith(
          { [BASIS_PROCUREMENT_PARAM]: starting.id },
          t("I want to use the procurement «{{title}}» as the starting point. Help me through it, one step at a time.", {
            title: starting.title,
          }),
          t("An earlier procurement or contract"),
          actions.send,
        );
        return;
      case "contract":
        openWith(
          { [BASIS_CONTRACT_PARAM]: starting.id },
          t("I want to use the contract «{{title}}» as the starting point. Help me through it, one step at a time.", {
            title: starting.title,
          }),
          t("An earlier procurement or contract"),
          actions.send,
        );
        return;
      case "nothing":
        actions.send(
          t("I have nothing to start from. Ask me what you need to know, one question at a time."),
          String(t("Nothing to start from")),
        );
    }
  };

  return (
    <>
      <AssistantChat
        heading={{
          title: t("Create with AI"),
          subtitle: t("The AI fills in the procurement for you and guides you through the rest"),
          hidesSubtitleOnPhone: true,
          action: procurementId ? (
            <Button
              variant="link"
              colorScheme="teal"
              size="sm"
              fontWeight="normal"
              onClick={() => void continueInForm()}
              isLoading={isSavingDraft}
              isDisabled={!position && !procurementFailedToLoad}
            >
              {t("Continue in the form")}
            </Button>
          ) : (
            <Button
              variant="link"
              colorScheme="teal"
              size="sm"
              fontWeight="normal"
              onClick={fillInYourself.start}
              isLoading={fillInYourself.isCreating}
            >
              {t("Fill in yourself")}
            </Button>
          ),
        }}
        inputBlockedReason={
          awaitsRegulation ? String(t("Confirm the regulations and procedure above to continue.")) : undefined
        }
        aboveConversation={(actions) => (
          <>
            <CreationProgress
              procurementId={procurementId}
              isDisabled={actions.disabled || awaitsRegulation}
              regulationAwaitsConfirmation={awaitsRegulation}
              onRevisit={(area, request) => {
                if (area === "regulation" && regulation?.reopened) {
                  const eventId = regulation.event.id;
                  setConfirmedRegulationFor((confirmed) => confirmed.filter((id) => id !== eventId));
                  setReopenedRegulationFor((reopened) => [...reopened, eventId]);
                  return;
                }
                actions.send(request, String(t("Change an area")));
              }}
            />
            {regulation && confirmation && awaitsRegulation && (
              <RegulationConfirmationCard
                event={regulation.event}
                confirmation={confirmation}
                isDisabled={actions.disabled}
                onConfirmed={async (procedure) => {
                  const eventId = regulation.event.id;
                  try {
                    if (isUnsaved) await saveAsDraft(eventId);
                  } catch (e) {
                    trigger(e);
                    return;
                  }
                  setConfirmedRegulationFor((confirmed) => [...confirmed, eventId]);
                  setReopenedRegulationFor((reopened) => reopened.filter((id) => id !== eventId));
                  actions.send(confirmedRegulationMessage(confirmation, procedure), String(t("Regulations confirmed")));
                }}
              />
            )}
          </>
        )}
        emptyState={(actions) => (
          <WhatDoYouHave
            actions={actions}
            onStart={(starting) => void start(starting, actions)}
            isStarting={isCreatingFromNeed || opening !== null}
          />
        )}
      />
      {leaveGuard}
      {fillInYourself.isChoosingDepartment && (
        <ChooseEventDepartmentModal
          isOpen
          departments={fillInYourself.departments}
          isCreating={fillInYourself.isCreating}
          onCancel={fillInYourself.cancelChoosingDepartment}
          onConfirm={fillInYourself.confirmDepartment}
        />
      )}
    </>
  );
};
